import '@assets/styles/Style.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './index.css';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';

import { isSafari } from '@utils/browserDetect';

import { HaloLink } from '@components/HaloLink';
import { useGenerationStatus } from '@hooks/apiHooks/generationsHooks';
import { usePersonaBySlug } from '@hooks/apiHooks/personasHooks';
import { useGetUser } from '@hooks/apiHooks/userHooks';
import { useArtifacts } from '@hooks/storeHooks/useArtifacts';
import { useGenerationState } from '@hooks/storeHooks/useGenerationState';
import {
  useAudioVideo,
  useSessionFeatureState,
} from '@hooks/storeHooks/useSessionState';
import { isAdmin } from '@utils/authUtils';
import { Features } from '../../config';
import { useAuth } from 'react-oidc-context';
import Artifacts from './Artifacts';
import SessionBackground from './Background/Background';
import { CompanionInfo } from './CompanionInfo';
import { CorrectDTButton } from './CorrectDTButton';
import { ErrorMessage } from './ErrorMessage';
import { Generation } from './Generation';
import { PromptView } from './PromptView';
import { SuggestedMentorsPanel } from './SuggestedMentors';

const HALO_WINDOW_OPEN_KEY = 'haloWindowOpen';

const Session: React.FC = () => {
  const { t } = useTranslation();
  const { slug, taskId } = useParams<{ slug: string; taskId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const startParam = searchParams.get('start');
  const isDesignerSession = searchParams.get('designSession') === 'true';
  const isCapabilitySession = !!taskId || isDesignerSession;
  const userInput = searchParams.get('userInput');
  const isMusicGenerationSession = !!taskId;
  const { user } = useAuth();

  const [appLoading, setAppLoading] = useState(true);
  const [haloWindowOpen] = useSessionStorage<string | null>(
    HALO_WINDOW_OPEN_KEY,
    null,
  );

  const { isRegeneratingMusic, isRegeneratingLyrics } = useGenerationState();
  const { suggestedMentors, setSuggestedMentors } = useArtifacts();
  const { webRTCStatus } = useAudioVideo();
  const { data: userData } = useGetUser();

  const {
    companionSlug,
    companionData,
    avatarReady,
    secondsBalance,
    isTwinUpdating,
    setCompanionSlug,
    setTaskId,
    setIsDesignerSession,
    setIsConnecting,
    setAutoConnect,
    resetSessionState,
  } = useSessionFeatureState();

  const { data: musicGeneration } = useGenerationStatus(
    isMusicGenerationSession ? taskId || null : null,
    isMusicGenerationSession,
    isRegeneratingMusic,
    isRegeneratingLyrics,
  );

  // Check if companion slug failed to load (404 / mentor not found)
  const { isError: isCompanionError, isFetching: isCompanionFetching } =
    usePersonaBySlug(companionSlug || '');

  // Handle HaloLink callback when app is opened
  const handleHaloLinkLoad = () => {
    setAppLoading(false);
  };

  useEffect(() => {
    if (slug && slug !== companionSlug && secondsBalance !== null) {
      // Safari requires a user gesture for both getUserMedia() and audio
      // playback.  The gesture from clicking a companion card on the main page
      // expires during React renders + API fetches, so auto-connect from a
      // useEffect is unreliable on Safari regardless of navigation type.
      // Force the user to tap "Start call" to provide a fresh gesture.
      const shouldAutoConnect =
        startParam === 'true' && secondsBalance > 0;
      const safariBlocked = shouldAutoConnect && isSafari();

      if (safariBlocked) {
        // Remove ?start=true so the UI shows the manual "Start call" button
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('start');
          return next;
        }, { replace: true });
      }

      const autoConnectAllowed = shouldAutoConnect && !safariBlocked;

      resetSessionState(false);
      setCompanionSlug({
        companionSlug: slug,
        isSessionPage: true,
        autoConnect: autoConnectAllowed,
      });
      setIsConnecting(autoConnectAllowed);
    }
  }, [slug, secondsBalance]);

  useEffect(() => {
    setTaskId(taskId || null);
    setIsDesignerSession(isDesignerSession);
  }, [taskId, isDesignerSession, setTaskId, setIsDesignerSession]);

  // Handle HaloLink app window detection
  useEffect(() => {
    if (companionData?.mentorId && haloWindowOpen === companionData.mentorId) {
      setAppLoading(false);
    }
  }, [haloWindowOpen, companionData?.mentorId]);

  useEffect(() => {
    document.body.classList.add('session-page');

    return () => {
      resetSessionState(true);
      setSuggestedMentors(null);
      document.body.classList.remove('session-page');
    };
  }, []);

  useEffect(() => {
    if (webRTCStatus?.blocked) {
      setIsConnecting(false);
      setAutoConnect(false);
    }
  }, [webRTCStatus, setIsConnecting, setAutoConnect]);

  if (webRTCStatus?.blocked) {
    return <ErrorMessage />;
  }

  // Show error when companion/mentor not found (404)
  if (isCompanionError && !isCompanionFetching && !companionData) {
    return createPortal(
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-99999">
        <div className="text-center max-w-lg px-6">
          <img
            src="/assets/images/logo_napster_white.svg"
            className="h-[26px] mx-auto mb-10"
            alt="Napster"
          />
          <p className="text-white text-2xl md:text-3xl font-bold leading-snug mb-8">
            {t('session.companionNotFound')}
          </p>
          <Link
            to="/"
            className="px-8 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-lg text-sm font-medium transition-colors duration-200">
            {t('session.goHome')}
          </Link>
        </div>
      </div>,
      document.body,
    );
  }

  const isCompanionTwin = userData?.data?.mentorId === companionData?.mentorId;

  return (
    <>
      {isAdmin(user) && (
        <PromptView
          mentorId={companionData?.mentorId}
          personaData={companionData}
        />
      )}
      <SessionBackground
        src={companionData?.avatar?.backgroundImageUrl || ''}
        showPulse={isTwinUpdating}
      />
      {isMusicGenerationSession && (
        <Generation
          musicGeneration={musicGeneration}
          fanSlug={companionSlug || ''}
        />
      )}
      {isDesignerSession && (
        <Generation
          userInput={userInput || undefined}
          fanSlug={companionSlug || ''}
        />
      )}
      {companionData && (
        <>
          {!startParam && <CompanionInfo companionData={companionData} />}
          <>
            {avatarReady && (
              <>
                {!isCapabilitySession && (
                  <>
                    <Artifacts mentorId={companionData.mentorId} />
                    {!!suggestedMentors?.length && <SuggestedMentorsPanel />}
                  </>
                )}
              </>
            )}
          </>

          {/* HaloLink for native macOS app integration */}
          {companionData.mentorId && appLoading && (
            <HaloLink
              mentorId={companionData.mentorId}
              showLink={false}
              onLoad={handleHaloLinkLoad}
            />
          )}
        </>
      )}

      {/* CorrectDTButton positioned at bottom center */}
      {Features.enableCorrectMeButton &&
        companionData &&
        avatarReady &&
        !isCapabilitySession &&
        isCompanionTwin && (
          <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-[1001]">
            <CorrectDTButton />
          </div>
        )}
    </>
  );
};

export default Session;
